export interface Bounds {
  /** All normalised 0..1 of the canvas, so canvases of different sizes compare directly. */
  x0: number
  y0: number
  x1: number
  y1: number
}

export const EMPTY_BOUNDS: Bounds = { x0: 0, y0: 0, x1: 0, y1: 0 }

const ALPHA_FLOOR = 8

/**
 * The box the artwork actually occupies, from the alpha channel.
 *
 * This is what tells a nose from a hairstyle without looking at a single pixel's colour: hair fills
 * the top of the frame, a nose is a small patch near the middle. Every layer of one option shares
 * one canvas, so the box is comparable between options of the same section.
 */
export function alphaBounds(
  data: Uint8ClampedArray | number[],
  width: number,
  height: number,
): Bounds {
  if (!width || !height || data.length < width * height * 4) {
    return EMPTY_BOUNDS
  }
  let minX = width
  let minY = height
  let maxX = -1
  let maxY = -1

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      if (data[(y * width + x) * 4 + 3] > ALPHA_FLOOR) {
        if (x < minX) {
          minX = x
        }
        if (x > maxX) {
          maxX = x
        }
        if (y < minY) {
          minY = y
        }
        if (y > maxY) {
          maxY = y
        }
      }
    }
  }

  if (maxX < 0) {
    return EMPTY_BOUNDS
  }

  return {
    x0: minX / width,
    y0: minY / height,
    x1: (maxX + 1) / width,
    y1: (maxY + 1) / height,
  }
}

export const isEmptyBounds = (b: Bounds): boolean => b.x1 <= b.x0 || b.y1 <= b.y0

const centre = (b: Bounds) => ({
  x: (b.x0 + b.x1) / 2,
  y: (b.y0 + b.y1) / 2,
})

const area = (b: Bounds) => Math.max(0, b.x1 - b.x0) * Math.max(0, b.y1 - b.y0)

/** How far the two boxes' centres sit apart, as a fraction of the canvas diagonal. */
export function centreDistance(a: Bounds, b: Bounds): number {
  const ca = centre(a)
  const cb = centre(b)

  return Math.hypot(ca.x - cb.x, ca.y - cb.y) / Math.SQRT2
}

/** Intersection over union. 1 means the same box, 0 means no overlap at all. */
export function overlap(a: Bounds, b: Bounds): number {
  const ix = Math.max(0, Math.min(a.x1, b.x1) - Math.max(a.x0, b.x0))
  const iy = Math.max(0, Math.min(a.y1, b.y1) - Math.max(a.y0, b.y0))
  const inter = ix * iy
  const union = area(a) + area(b) - inter

  return union <= 0 ? 0 : inter / union
}

export const CENTRE_LIMIT = 0.18
export const OVERLAP_FLOOR = 0.25

export type PlacementVerdict =
  | { looksRight: true }
  | { looksRight: false; reason: 'farFromOthers' | 'barelyOverlaps'; centre: number; overlap: number }
  | { looksRight: true; reason: 'noReference' }

/**
 * Does this artwork sit where this section's artwork sits?
 *
 * A HEURISTIC, and deliberately a warning rather than a block: art direction is allowed to
 * surprise us, and a false block would be worse than a false warning. It exists because a nose
 * uploaded into the hair section passes every other check — the filenames are fine, the PNG is
 * real, the dimensions are right — and only shows up as a broken avatar after publishing.
 */
export function checkPlacement(
  candidate: Bounds,
  reference?: Bounds,
): PlacementVerdict {
  if (!reference || isEmptyBounds(reference) || isEmptyBounds(candidate)) {
    return { looksRight: true, reason: 'noReference' }
  }
  const c = centreDistance(candidate, reference)
  const o = overlap(candidate, reference)

  if (c > CENTRE_LIMIT) {
    return { looksRight: false, reason: 'farFromOthers', centre: c, overlap: o }
  }
  if (o < OVERLAP_FLOOR) {
    return { looksRight: false, reason: 'barelyOverlaps', centre: c, overlap: o }
  }

  return { looksRight: true }
}
